import { removeAuthentication } from '../auth-helper';

type EncryptWorkerResponse =
  | { type: 'ready' }
  | { type: 'init-error'; error: string }
  | { type: 'result'; id: number; payload: string }
  | { type: 'error'; id: number; error: string };

type PendingRequest = {
  resolve: (value: string) => void;
  reject: (error: Error) => void;
};

export default class EncryptStorage {
  private key: CryptoKey | null = null;
  private secret: string;
  private worker: Worker | null = null;
  private workerReady: Promise<void> = Promise.resolve();
  private workerReadyResolve?: () => void;
  private pendingRequests = new Map<number, PendingRequest>();
  private requestId = 0;

  constructor(secret: string) {
    this.secret = secret;

    if (typeof window !== 'undefined' && typeof window.Worker !== 'undefined') {
      this.initializeWorker();
    }
  }

  private initializeWorker(): void {
    try {
      this.worker = new Worker(new URL('./encrypt.worker.ts', import.meta.url));
    } catch {
      this.worker = null;
      this.workerReady = Promise.resolve();
      return;
    }

    this.workerReady = new Promise((resolve) => {
      this.workerReadyResolve = resolve;
    });

    this.worker.addEventListener('message', this.handleWorkerMessage);
    this.worker.addEventListener('messageerror', this.handleWorkerMessageError);
    this.worker.addEventListener('error', this.handleWorkerError);

    this.worker.postMessage({ type: 'init', secret: this.secret });
  }

  private handleWorkerMessage = (
    event: MessageEvent<EncryptWorkerResponse>
  ): void => {
    const message = event.data;

    if (message.type === 'ready') {
      this.workerReadyResolve?.();
      this.workerReadyResolve = undefined;
      return;
    }

    if (message.type === 'init-error') {
      this.workerReadyResolve?.();
      this.workerReadyResolve = undefined;
      this.disableWorker(message.error);
      return;
    }

    if (!('id' in message)) {
      return;
    }

    const pending = this.pendingRequests.get(message.id);
    if (!pending) {
      return;
    }

    this.pendingRequests.delete(message.id);

    if (message.type === 'result') {
      pending.resolve(message.payload);
    } else {
      if (message.type === 'error') {
        // send empty parameter to remove auth in prod (automatically for domain .mobitech.id)
        // use param '.waresix.com' for test & sit environment
        // use param 'localhost' for local development
        removeAuthentication();
        setTimeout(() => {
          window.location.reload();
        }, 600);
      }
      pending.reject(new Error(message.error));
    }
  };

  private handleWorkerError = (event: ErrorEvent): void => {
    const message = event?.message || 'Encryption worker error';
    this.disableWorker(message);
  };

  private handleWorkerMessageError = (): void => {
    this.disableWorker('Failed to process message from encryption worker');
  };

  private disableWorker(reason?: string): void {
    if (this.worker) {
      this.worker.removeEventListener('message', this.handleWorkerMessage);
      this.worker.removeEventListener(
        'messageerror',
        this.handleWorkerMessageError
      );
      this.worker.removeEventListener('error', this.handleWorkerError);
      this.worker.terminate();
      this.worker = null;
    }

    if (this.workerReadyResolve) {
      this.workerReadyResolve();
      this.workerReadyResolve = undefined;
    }

    this.workerReady = Promise.resolve();

    if (this.pendingRequests.size > 0) {
      const error = this.createWorkerError(reason);
      this.pendingRequests.forEach(({ reject }) => reject(error));
      this.pendingRequests.clear();
    }
  }

  private async callWorker(
    type: 'encrypt' | 'decrypt',
    payload: string
  ): Promise<string> {
    const worker = this.worker;

    if (!worker) {
      throw this.createWorkerError();
    }

    await this.workerReady;

    if (!this.worker) {
      throw this.createWorkerError();
    }

    return new Promise((resolve, reject) => {
      const id = ++this.requestId;
      this.pendingRequests.set(id, { resolve, reject });
      try {
        worker.postMessage({ type, id, payload });
      } catch (error) {
        this.pendingRequests.delete(id);
        if (this.shouldDisableWorker(error)) {
          this.disableWorker(
            error instanceof Error ? error.message : String(error)
          );
        }
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  private shouldDisableWorker(error: unknown): boolean {
    if (!(error instanceof Error)) return true;
    if (error.name === 'EncryptWorkerError') return true;
    return error.message.toLowerCase().includes('encryption worker');
  }

  private createWorkerError(reason?: string): Error {
    const error = new Error(reason || 'Encryption worker unavailable');
    error.name = 'EncryptWorkerError';
    return error;
  }

  private async getKey(): Promise<CryptoKey> {
    if (this.key) return this.key;

    const encoder = new TextEncoder();

    const material = await crypto.subtle.importKey(
      'raw',
      encoder.encode(this.secret),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    this.key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode('zustand-encrypt-salt'),
        iterations: 100000,
        hash: 'SHA-256',
      },
      material,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    return this.key;
  }

  private async encryptValueLocal(value: string): Promise<string> {
    const cryptoKey = await this.getKey();
    const encoder = new TextEncoder();

    const iv = crypto.getRandomValues(new Uint8Array(12)); // required
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encoder.encode(value)
    );

    const encryptedBytes = new Uint8Array(encrypted);
    const combined = new Uint8Array(iv.length + encryptedBytes.length);
    combined.set(iv, 0);
    combined.set(encryptedBytes, iv.length);

    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < combined.length; i += chunkSize) {
      const chunk = combined.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  }

  private async decryptValueLocal(combinedCipher: string): Promise<string> {
    const cryptoKey = await this.getKey();

    const sanitizedCipher =
      typeof combinedCipher === 'string'
        ? combinedCipher.trim()
        : combinedCipher;

    let binary: string;
    try {
      binary = atob(sanitizedCipher);
    } catch {
      return combinedCipher;
    }

    const combinedBytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));

    const iv = combinedBytes.slice(0, 12);
    const encryptedBytes = combinedBytes.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encryptedBytes
    );

    return new TextDecoder().decode(decrypted);
  }

  async encryptValue(value: string): Promise<string> {
    if (this.worker) {
      try {
        return await this.callWorker('encrypt', value);
      } catch (error) {
        if (this.shouldDisableWorker(error)) {
          this.disableWorker((error as Error).message);
        } else {
          throw error;
        }
      }
    }

    return this.encryptValueLocal(value);
  }

  async decryptValue(combinedCipher: string): Promise<string> {
    if (this.worker) {
      try {
        return await this.callWorker('decrypt', combinedCipher);
      } catch (error) {
        if (this.shouldDisableWorker(error)) {
          this.disableWorker((error as Error).message);
        } else {
          throw error;
        }
      }
    }

    return this.decryptValueLocal(combinedCipher);
  }

  async setItem(key: string, value: string): Promise<void> {
    const encryptedString = await this.encryptValue(value);
    localStorage.setItem(key, encryptedString);
  }

  async getItem(key: string): Promise<string | null> {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    try {
      return await this.decryptValue(stored);
    } catch {
      return null;
    }
  }

  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  }
}
